import React, { FC, useState, useEffect } from 'react';
import { CircularProgress } from '@material-ui/core';
import { StatusItem, useNotificationsContext } from 'shared/context/notificationContext';

interface TrafficLightIdItemProps {
  id: number;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  isSelected?: boolean;
  animate?: boolean;
}

const TrafficLightIdItem: FC<TrafficLightIdItemProps> = ({
  id,
  onClick,
  isSelected = false,
  animate = false
}) => {
  const [isHoveredId, setIsHoveredId] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={(event) => {
        setIsHoveredId(true);
        event.stopPropagation();
      }}
      onMouseLeave={(event) => {
        setIsHoveredId(false);
        event.stopPropagation();
      }}
      className="flex items-center justify-center text-center rounded-full p-2"
      style={{
        cursor: 'pointer',
        height: 'full',
        color: 'rgba(255, 0, 0, 1)',
        fontSize: '18px',
        fontWeight: 'bold',
        border: isHoveredId || isSelected ? '1px solid rgba(255, 0, 0)' : 'none',
        boxShadow:
          isHoveredId || isSelected ? 'inset 0 0 6px 3px rgba(255, 0, 0)' : '',
        animation: animate ? 'redTrafficLightPulse 1.3s cubic-bezier(0, 2, 0.5, 1) infinite' : '',
        transition: 'all 0.15s ease'
      }}
    >
      {id}
    </div>
  );
};

interface RedTrafficLightProps {
  total: number;
  statuses: StatusItem[];
  onClick?: (id?: number) => void;
  isActive?: boolean;
  reservationIdSelected?: number | null;
  animate?: boolean;
  isLoading?: boolean;
}

const RedTrafficLight: FC<RedTrafficLightProps> = ({
  total,
  statuses,
  onClick,
  isActive = false,
  reservationIdSelected = null,
  animate = false,
  isLoading = false
}) => {
  const { totalStatesReservations } = useNotificationsContext();
  const {reservationStateChanged} = totalStatesReservations;
  const [isHovered, setIsHovered] = useState(false);
  const shouldHighlight = isActive || isHovered;
  const hasItems = total > 0;
  useEffect(() => {
    if (!document.getElementById('redTrafficLightShadowPulse')) {
      const style = document.createElement('style');
      style.id = 'redTrafficLightShadowPulse';
      style.textContent = `
        @keyframes redTrafficLightPulse {
          0%, 100% {
            box-shadow: 0 0 12px 6px rgba(255, 0, 0, 1);
            opacity: 1;
          }
          50% {
            box-shadow: 0 0 0px 0px rgba(255, 0, 0, 1);
            opacity: 1;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <div className=" flex flex-col items-center justify-center ">
      <div
        onClick={() => hasItems && onClick?.()}
        onMouseEnter={() => hasItems && setIsHovered(true)}
        onMouseLeave={() => hasItems && setIsHovered(false)}
        className={`flex items-center justify-center text-center rounded-full relative ${hasItems ? 'bg-redTrafficLight' : '#080808'}`}
        style={{
          color: '#000000',
          width: '33px',
          height: '33px',
          fontSize: '14px',
          fontWeight: 'bold',
          cursor: hasItems && onClick ? 'pointer' : 'default',
          boxShadow: shouldHighlight ? '0 0 14px 3px rgba(255, 0, 0)' : '',
          transform: shouldHighlight ? 'scale(1.1)' : 'scale(1)',
          transition: 'all 0.15s ease',
        }}
      >
        {isLoading
          ? <CircularProgress size={16} style={{ color: 'rgba(255, 0, 0, 1)' }} />
          : total
        }
        <div
          className="flex items-center justify-center rounded-full"
          style={{
            position: 'absolute',
            top: '0px',
            left: '0px',
            backgroundColor: 'transparent',
            width: '33px',
            height: '33px',
            pointerEvents: 'none',
            zIndex: 1,
            animation: animate
              ? ' redTrafficLightPulse 1.3s cubic-bezier(0, 2, 0.5, 1) infinite '
              : ''
          }}
        />
      </div>
      {isHovered && hasItems && (
        <div
          onMouseEnter={(event) => {
            setIsHovered(true);
            event.stopPropagation();
          }}
          onMouseLeave={(event) => {
            setIsHovered(false);
            event.stopPropagation();
          }}
          className="text-sm grid gap-4 grid-cols-8"
          style={{
            background:
            'linear-gradient(180deg, #3a3a3a 0%, #1a1a1a 45%, #0a0a0a 100%)',
            position: 'absolute',
            top: '32px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            marginTop: '8px',
            padding: '8px',
            borderRadius: '10px',
            border: '2px solid #000',
            minWidth: '800px',
            maxHeight: '600px',
            overflowY: 'auto',
            boxShadow: '0 10px 10px 0 rgba(0, 0, 0, 0.5)'
          }}
        >
          {statuses.map((item) => (
            <div key={item.category} className="col-span-8 flex flex-col gap-1">
              <span
                style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  paddingLeft: '4px'
                }}
              >
                {item.category}
              </span>
              <div className="grid gap-2 grid-cols-8">
                {item.ids.map((id) => (
                  <TrafficLightIdItem
                    key={id}
                    id={id}
                    onClick={(event) => {
                      event.stopPropagation();
                      onClick?.(id);
                      setIsHovered(false);
                    }}
                    isSelected={reservationIdSelected === id}
                    animate={animate && id === reservationStateChanged?.id}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RedTrafficLight;
